import container from "@/lib/di/container";
import { Suspense, useEffect, useState } from "react";
import { FilterBar, FilterInputTemplate, FilterInputType } from "../../components/filter/FilterBar";
import { FilterSearchPayload, DataTable } from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { Button, Dropdown, Modal, message } from "antd";
import { DeleteOutlined, FileExcelOutlined, InfoCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { DeleteButton } from "../../components/table/DeleteButton";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { TransactionRepo } from "@/core/repos/tables/transaction.repo";
import { TransactionModel, TransactionModelFactory } from "@/core/models/transaction.model";
import { transactionExtraTypePipe } from "@/core/pipes/transaction-extra-type.pipe";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { usePermissionStore } from "@/core/stores/permission.store";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { useNavigate, useSearchParams } from "react-router";
import { TransactionExtraType } from "@/core/enums/global.enums";
import { GlobalUtils } from "../../utils/global.utils";

const transactionRepo = container.get(TransactionRepo);
const contractRepo = container.get(ContractRepo);

function TransactionHistory() {
    const titleData = useTitleDataStore();
    const permissionStore = usePermissionStore();
    const [searchParams] = useSearchParams();
    const recordId = searchParams.get('recordId');
    const objectType = searchParams.get('objectType');
    const [modalData, setModalData] = useState<TransactionModel>(TransactionModelFactory.getEmpty())
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map())
    const [selectedRows, setSelectedRows] = useState<TransactionModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const navigate = useNavigate();
    const [filterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập nội dung tìm kiếm",
            type: FilterInputType.text,
        },
        {
            dataIndex: "extraType",
            label: "Loại giao dịch",
            placeholder: "Chọn Loại giao dịch",
            type: FilterInputType.select,
            options: Object.keys(TransactionExtraType).map(key => ({ value: key, name: transactionExtraTypePipe(key) }))
        },
        {
            rangeIndex: {from: "fromDate", to: "toDate"},
            type: FilterInputType.dateRange,
            placeholder: "Từ lúc",
            secondaryPlaceholder: "Đến lúc",
            label: "Thời gian giao dịch",
            span: 2,
            showTime: true
        }
    ])

    const showDetail = (record: TransactionModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const getContractById = async (id: string) => {
        return await contractRepo.detail(id);
    }

    const init = async () => {
        titleData.setTitleActions({
            text: "Thêm Lịch sử giao dịch",
            onPress: () => {
                showDetail(TransactionModelFactory.getEmpty())
            }
        })
        if (objectType=="WaterUser"&&recordId) {
            titleData.setTitleBack(true);
            const contract = await getContractById(recordId);
            if (contract) {
                titleData.setTitleText(`Lịch sử giao dịch - ${contract.code} - ${contract.name}`)
            }
        }
    }

    useEffect(()=>{init()}, []);

    async function onFilterChange(map: Map<string, FilterSearchPayload>) {
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: TransactionModel[]) {
        setSelectedRows([...rows]);
    }

    const deleteSelected = async () => {
        setDeleting(true);
        const result = await transactionRepo.deleteMany(selectedRows);
        if(result.success>0){
            messageApi.success({
                content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`
            })
        } else {
            messageApi.error({
                content: `xoá thất bại`
            })
        }
        setDeleting(false);
        setSelectedRows([]);
    }

    const deleteOne = async (id: string) => {
        setDeleting(true);
        const result = await transactionRepo.delete(id);
        if (result?.code === "00") {
            messageApi.success({
                content: `Bạn đã xoá thành công`
            })
            setDeleting(false);
        } else {
            messageApi.error({
                content: result?.errorMessage ?? "xoá thất bại"
            })
            setDeleting(false);
        }
    }

    const handleDeleteClick = (record: TransactionModel) => {
        Modal.confirm({
            title: "Xoá giao dịch?",
            content: (
                <div>
                    <p>Bạn có chắc chắn muốn xoá giao dịch này?</p>
                    <div style={{ marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        <p style={{ margin: '4px 0' }}><strong>Loại giao dịch:</strong> {transactionExtraTypePipe(record.extraType)}</p>
                        <p style={{ margin: '4px 0' }}><strong>Người thụ hưởng:</strong> {record.beneficiaryName || 'N/A'}</p>
                        <p style={{ margin: '4px 0' }}><strong>Số tiền:</strong> {record.amount.toLocaleString()}</p>
                        <p style={{ margin: '4px 0' }}><strong>Thời gian giao dịch:</strong> {formatDateTimePipe(record.createdAt)}</p>
                        {record.information && <p style={{ margin: '4px 0' }}><strong>Thông tin:</strong> {record.information}</p>}
                    </div>
                </div>
            ),
            okText: "Xoá",
            cancelText: "Huỷ",
            okType: "danger",
            width: 500,
            onOk: () => deleteOne(record.id)
        });
    }
    const showReports = () => {
        const params = GlobalUtils.buildQueryParamsFromFilterMap(filterMap, { type: 4 });
        navigate(encodeURI(`/billing/billing-report?${params}`));
    }

    return (
    <div className="px-4 py-6">

        
        {contextHolder}
        <FilterBar setMap={setFilterMap} onChange={onFilterChange} templates={filterTemplates} map={filterMap} actions={[
            <div key={1}>
                <Button type="primary" icon={<FileExcelOutlined />} style={{ background: "#29A339"}} onClick={showReports}>Xuất Excel</Button>
                <DeleteButton loading={deleting} length={selectedRows.length} onConfirm={deleteSelected} />
            </div>
        ]}/>
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <DataTable<TransactionModel>
                columns={[
                    { dataIndex: 'beneficiaryName', name: 'Người thụ hưởng' },
                    { dataIndex: 'amount', name: 'Số tiền giao dịch', pipe: formatCurrencyPipe },
                    { dataIndex: 'currentBalance', name: 'Số dư hiện tại', pipe: formatCurrencyPipe },
                    { dataIndex: 'extraType', name: 'Loại giao dịch', pipe: transactionExtraTypePipe},
                    { dataIndex: 'createdAt', name: 'Thời gian giao dịch', pipe: formatDateTimePipe},
                    { dataIndex: 'actualReceivedDate', name: 'Ngày nhận tiền thực tế', pipe: formatDateTimePipe},
                    { dataIndex: 'createdBy', name: 'Người tạo' },
                    { dataIndex: 'information', name: 'Thông tin giao dịch' },
                    {
                        name: 'Thao tác',
                        render: (_, record) => {
                            const menuItems = [
                                {
                                    key: '1',
                                    label: <p>Xem chi tiết</p>,
                                    icon: <InfoCircleOutlined />,
                                    onClick: () => showDetail(record)
                                }
                            ];
                            
                            if (permissionStore.page.delete) {
                                menuItems.push({
                                    key: '2',
                                    label: <p style={{ color: '#ff4d4f' }}>Xoá</p>,
                                    icon: <DeleteOutlined />,
                                    onClick: () => handleDeleteClick(record)
                                });
                            }

                            return (
                                <div className="flex flex-row px-1 items-center justify-center">
                                    <Dropdown menu={{ items: menuItems }} trigger={["hover"]}>
                                        <Button type="text" icon={<MoreOutlined />} />
                                    </Dropdown>
                                </div>
                            );
                        },
                        fixed: "right",
                    },
                ]}
                repository={transactionRepo}
                filterMap={filterMap}
                onSelect={onRowSelect}
                defaultParams={{recordId, objectType}}
            ></DataTable>
        </div>
        <DetailModal isModalOpen={isDetailModal} setIsModalOpen={setDetailModal} data={modalData}></DetailModal>
    </div>)
}

export default function TransactionHistoryPage() {
    return <Suspense>
        <TransactionHistory />
    </Suspense>
}